import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFolder } from "src/core/contexts/FolderContext";
import {
  RenewMissionFormValues,
  renewMissionSchema,
} from "src/core/forms/missions/renewMission.schema";
import { DataType, MissionStatus, useUpdateMissionMutation } from "src/core/gql/graphql";
import { IMission } from "src/core/hooks/folder/useFolderMissions";
import { useToast } from "src/core/hooks/use-toast";

export const useRenewMission = (
  setOpen: (open: boolean) => void,
  mission: IMission | null,
  setMissionToEdit: (mission: IMission | null) => void
) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { refetch } = useFolder();

  const form = useForm<RenewMissionFormValues>({
    resolver: zodResolver(renewMissionSchema(t)),
    mode: "onSubmit",
    defaultValues: {
      renewDate: "",
    },
  });
  const [updateMission, { loading: isUpdating }] = useUpdateMissionMutation();
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = useCallback(
    async (data: RenewMissionFormValues) => {
      try {
        setIsLoading(true);

        if (!mission) {
          throw new Error("Aucune mission trouvée");
        }

        await updateMission({
          variables: {
            missionId: mission?.id,
            inputValues: [
              { fieldCode: "renewDate", value: data.renewDate, dataType: DataType.Timestamp },
            ],
            shouldReopen: mission.status === MissionStatus.Closed,
          },
        });

        await refetch();

        toast({
          description: t("form.renewMission.success"),
        });

        // Réinitialiser le formulaire
        form.reset({
          renewDate: "",
        });
      } catch (error) {
        console.error("useRenewMissionForm::error", error);
        toast({
          description: t("form.renewMission.fail"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        setMissionToEdit(null);
        setOpen(false);
      }
    },
    [toast, t, setOpen, form, refetch, mission, updateMission, setMissionToEdit]
  );

  return {
    form,
    onSubmit,
    isLoading: isUpdating || isLoading,
  };
};
